import { useTranslation } from 'react-i18next'

import { EmptyPanel } from '@/components/px/EmptyPanel'
import { ErrorPanel } from '@/components/px/ErrorPanel'
import { ForbiddenPanel } from '@/components/px/ForbiddenPanel'
import { LoadingPanel } from '@/components/px/LoadingPanel'
import type { ProjectsError } from '@/features/projects/slices/projectsSlice'
import { asApiError } from '@/services/apiError'

/**
 * The portfolio board's loading / error / empty triad (PX-W6.3, AC-1).
 *
 * ⚠ PX-W6.6 (AC-2) turned these from hand-rolled markup into thin wrappers over
 * the SHARED panels. They still exist — the page composes them inside its tab
 * furniture and they carry the portfolio's own copy — but the markup, the a11y
 * roles and the `data-triad` markers now come from one place, so the triad
 * audit can prove the board renders the shared states rather than three
 * look-alikes that drift. Do not re-inline the markup.
 */

export function PortfolioLoading() {
  const { t } = useTranslation('portfolio')
  return <LoadingPanel label={t('states.loading')} />
}

export interface PortfolioErrorProps {
  /** The slice's stored error, status included. */
  error: ProjectsError
  onRetry: () => void
}

/**
 * A failure on the board — or the SERVER'S REFUSAL, which is a different fact
 * and gets a different panel (AC-6.2).
 *
 * ⚠ The status must travel. This component used to take only the message key
 * and hand `ErrorPanel` a hardcoded `status: 500`, so a 403 arrived here
 * indistinguishable from a crash and the board answered "something went wrong,
 * try again" to a caller who was refused — a retry that can never succeed
 * (merged review C2).
 */
export function PortfolioError({ error, onRetry }: PortfolioErrorProps) {
  if (error.status === 403) {
    return (
      <div data-testid="portfolio-error">
        <ForbiddenPanel requested="/{orgId}/projects" />
      </div>
    )
  }
  return (
    <div data-testid="portfolio-error">
      <ErrorPanel error={asApiError(error)} onRetryClicked={onRetry} />
    </div>
  )
}

export function PortfolioEmpty() {
  const { t } = useTranslation('portfolio')
  return (
    <div data-testid="portfolio-empty">
      <EmptyPanel title={t('states.emptyTitle')} body={t('states.emptyBody')} />
    </div>
  )
}
